import * as Cookies from 'js-cookie';
import storage, { sessionStorage } from 'utils/storage';
import logger from 'utils/logger';
import { Query, RouterLocation } from 'router';
import * as routerActions from 'router/actions';
import * as messageActions from './messages';
import * as campaignActions from './campaign';
import * as offersActions from './offers';
import { apiNetworkRequest, networkRequest, AppAction, AppAsyncAction } from './helpers';
import { parseJwt } from 'utils/helpers';

export const SET_AUTH = 'auth/SET_AUTH';

interface SetAuthAction {
  type: typeof SET_AUTH
  sessionToken: string
  userId: string
  isAnonymousUser: boolean
}

export const setAuth = (sessionToken: string, userId: string, isAnonymousUser = false): SetAuthAction => ({
  type: SET_AUTH,
  sessionToken,
  userId,
  isAnonymousUser,
});

export const SET_IS_BOT = 'auth/SET_IS_BOT';

interface SetIsBotAction {
  type: typeof SET_IS_BOT
  isBot: boolean
}

export const setIsBot = (isBot: boolean): SetIsBotAction => ({
  type: SET_IS_BOT,
  isBot,
});

export const decodeToken = (token: string): { userId: string, expDate: Date, isAnonymous: boolean } | null => {
  const decodedJwtToken = parseJwt(token);
  if (decodedJwtToken) {
    const { exp } = decodedJwtToken;
    let expDate: Date;
    
    // Validate that exp exists and is a valid number before creating Date
    if (typeof exp === 'number' && Number.isFinite(exp)) {
      expDate = new Date(exp * 1000); // Convert seconds to milliseconds
    } else {
      // Default to 30 days from now if exp is missing or invalid
      expDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    }

    return { userId: decodedJwtToken.userId, expDate, isAnonymous: String(decodedJwtToken.anonymous).toLowerCase() === 'true' };
  }
  return null;
};

export function authenticate(userId: string, token: string): SetAuthAction {
  const isHttps = window.location.protocol === 'https:';

  const { userId: tokenUserId, expDate, isAnonymous } = decodeToken(token) || {};
  if (userId !== tokenUserId) {
    throw Error('Invalid token');
  }

  const options = {
    expires: expDate,
    secure: isHttps,
    sameSite: isHttps ? 'None' as const : undefined,
  };

  Cookies.set('userId', userId, options);
  Cookies.set('token', token, options);

  return setAuth(token, userId, isAnonymous);
}

export function openInitialView(): AppAction {
  return (dispatch, getState) => {
    const { redirectTo = null } = getState().router.query;
    let to = { name: 'home' };

    if (typeof redirectTo === 'string') {
      try {
        to = JSON.parse(redirectTo) || { name: 'home' };
        to.name = to.name || 'home';
      } catch (e) {
        logger.error('Failed to parse redirectTo', e);
      }
    }

    return dispatch(routerActions.push(to));
  };
}

export function getCheckoutFirstRoute(query: Query): AppAsyncAction<RouterLocation> {
  return async (dispatch, getState) => {
    await dispatch(offersActions.fetchPurchaseFlowOffers());
    const { offers } = getState();

    const predefinedOffer = offers.find(offer => offer.id === query.packageId);

    if (!!query.packageId && !predefinedOffer) {
      dispatch(messageActions.addMessage({
        contentId: 'offer.noAvailableInRegion',
        duration: 10,
      }));

      return {
        name: 'checkout',
        query: {
          ...query,
          packageId: undefined,
        },
      };
    }

    if (predefinedOffer && !query.promoCode) {
      return {
        name: 'checkout-confirmation',
        query,
      };
    }

    if (predefinedOffer && query.promoCode) {
      try {
        const validationResult = await dispatch(
          campaignActions.validatePromocode((query.packageId || '').toString(), query.promoCode.toString()),
        );

        if (validationResult.valid) {
          return {
            name: 'checkout-confirmation',
            query,
          };
        }

        return {
          name: 'checkout',
          query: {
            ...query,
            packageId: undefined,
          },
        };
      } catch (e) {
        return {
          name: 'checkout',
          query: {
            ...query,
            promoCode: undefined,
          },
        };
      }
    }

    if (offers.length === 1) {
      const packageId = offers[0].id;
      return {
        name: 'checkout-confirmation',
        query: {
          ...query,
          packageId,
        },
      };
    }

    return {
      name: 'checkout',
      query,
    };
  };
}

export function openPurchaseFlow(query: Query): AppAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();

    const route = await dispatch(getCheckoutFirstRoute({
      ...state.router.query,
      ...query,
    }));

    dispatch(routerActions.push(route));
  };
}

export function login(fields: any, doAuthenticate = authenticate): AppAsyncAction {
  return async (dispatch, getState) => {
    const { loginMethod } = getState().settings.features;

    const response = await dispatch(networkRequest(
      loginMethod.url,
      loginMethod.httpMethod,
      {},
      fields,
    ));

    if (!response.ok) {
      throw new Error(`Login request failed: ${response.status} ${response.statusText}`);
    }

    const {
      userId,
      token,
    } = await response.json();

    dispatch(doAuthenticate(userId, token));
  };
}

export const LOGOUT = 'auth/LOGOUT';

interface LogoutAction {
  type: typeof LOGOUT
}

export function logout(inBrowser: boolean): AppAction {
  return (dispatch, getState) => {
    // Check if the user is anonymous - if so, don't remove cookies
    const { isAnonymousUser } = getState().auth;

    if (isAnonymousUser) {
      logger.warn('Attempted to logout an anonymous user - preventing cookie removal');
      // Still return the logout action to update the Redux state
      return dispatch({ type: LOGOUT });
    }

    // `Cookies` only works in the browser.
    // If this is triggered server-side the
    // expiration happens inside of magine-web's
    // onMatch callback.
    if (inBrowser) {
      Cookies.remove('userId');
      Cookies.remove('token');
      // API server set this cookies which are equal to token and userId
      // so we should remove them too
      Cookies.remove('mg_session_id');
      Cookies.remove('mg_user_id');

      sessionStorage.removeItem('paymentWarningNotificationShown');
      storage.clear();
    }

    return dispatch({ type: LOGOUT });
  };
}

export function requestPasswordChange(fields: any): AppAsyncAction<Response> {
  return async (dispatch, getState) => {
    const { language } = getState().settings.l10n;
    const { forgotPassword } = getState().settings.features.loginMethod;

    const response = await dispatch(networkRequest(
      forgotPassword.url,
      forgotPassword.httpMethod,
      {},
      {
        locale: language,
        ...fields,
      },
    ));

    return response;
  };
}

export function resetPassword(token: string, password: string): AppAsyncAction {
  return async (dispatch) => {
    await dispatch(apiNetworkRequest('auth.resetPassword', { token, password }));
  };
}

type SignupParams = {
  username: string,
  password: string,
  locale: string,
  geoipCountry: string,
  emailOptIn: boolean,
  name?: string,
  captcha?: {
    reCaptchaToken: string,
  },
};

export function signup(
  params: SignupParams,
  doAuthenticate = authenticate,
): AppAsyncAction {
  return async (dispatch, getState) => {
    const { registerMethods } = getState().settings.features;

    const {
      username: email,
      password,
      locale,
      geoipCountry,
      emailOptIn,
      name,
      ...dynamic
    } = params;

    const response = await dispatch(networkRequest(
      registerMethods.url,
      registerMethods.httpMethod,
      {},
      {
        identity: email,
        name: name || email, // there is no name for default SignIn but is for Social Account
        password,
        email,
        locale,
        country: geoipCountry,
        emailOptIn,
        ...dynamic,
      },
    ));

    const { userId, token } = await response.json();

    if (!response.ok || !userId || !token) {
      const error = new Error(`Signup request failed: ${response.status} ${response.statusText}`);
      throw error;
    }

    dispatch(doAuthenticate(userId, token));
  };
}

export type AuthActions = SetAuthAction | LogoutAction | SetIsBotAction;
